import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Clock, LogIn, LogOut, Coffee, Video, CalendarClock, Inbox, MessageSquare,
  CheckSquare, GraduationCap, Activity, Users, Plane, AlarmClock,
  ChevronRight, Loader2, Stethoscope, FileText, Monitor,
} from "lucide-react";
import api from "../../lib/api";
import { isMobileDevice } from "../../lib/device";

export const fmtHM = (iso) =>
  iso ? new Date(iso).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" }) : "";
export const fmtRel = (iso) => {
  if (!iso) return "";
  const sec = Math.floor((Date.now() - new Date(iso)) / 1000);
  if (sec < 60) return "ora";
  if (sec < 3600) return `${Math.floor(sec / 60)} min fa`;
  if (sec < 86400) return `${Math.floor(sec / 3600)} h fa`;
  const giorni = Math.floor(sec / 86400);
  if (giorni === 1) return "ieri";
  if (giorni < 7) return `${giorni} g fa`;
  if (giorni < 31) {
    const settimane = Math.floor(giorni / 7);
    return settimane === 1 ? "una settimana fa" : `${settimane} settimane fa`;
  }
  return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" });
};

/* ─── Shell riquadro ─── */
export const Card = ({ title, icon: Icon, action, children, testId, className = "" }) => (
  <section className={`bg-surface border border-border rounded-[10px] p-4 h-full flex flex-col ${className}`}
           data-testid={testId}>
    <div className="flex items-center justify-between gap-2 mb-3">
      <h2 className="text-sm font-semibold inline-flex items-center gap-1.5" style={{ color: "var(--wl-ink)" }}>
        {Icon && <Icon size={14} strokeWidth={1.8} aria-hidden="true" style={{ color: "var(--wl-text-muted)" }} />}
        {title}
      </h2>
      {action}
    </div>
    <div className="flex-1 min-h-0">{children}</div>
  </section>
);

const CardLink = ({ to, label, testId }) => (
  <Link to={to} data-testid={testId}
        className="text-xs inline-flex items-center gap-0.5 hover:underline shrink-0"
        style={{ color: "var(--wl-text-secondary)" }}>
    {label} <ChevronRight size={11} aria-hidden="true" />
  </Link>
);

export const EmptyState = ({ phrase, actionLabel, to, testId }) => (
  <div className="h-full flex flex-col items-start justify-center gap-1.5 py-3" data-testid={testId}>
    <div className="text-sm" style={{ color: "var(--wl-text-secondary)" }}>{phrase}</div>
    {actionLabel && to && (
      <Link to={to} className="text-xs font-medium hover:underline" style={{ color: "var(--wl-ink)" }}>
        {actionLabel} →
      </Link>
    )}
  </div>
);

export const CardSkeleton = ({ tall = false }) => (
  <div className={`bg-surface border border-border rounded-[10px] p-4 ${tall ? "min-h-[210px]" : "min-h-[150px]"}`}
       aria-hidden="true">
    <div className="animate-pulse space-y-3">
      <div className="h-3.5 w-1/3 rounded bg-subtle" />
      <div className="h-3 w-2/3 rounded bg-subtle" />
      <div className="h-3 w-1/2 rounded bg-subtle" />
      {tall && <div className="h-16 w-full rounded bg-subtle" />}
    </div>
  </div>
);

/* ─── La mia giornata (anello desktop / barra mobile) ─── */
export const MyDayCard = ({ data, onChanged, canClockMobile }) => {
  const [busy, setBusy] = useState(false);
  const clockAllowed = !isMobileDevice() || !!canClockMobile;
  const worked = data?.worked_minutes || 0;
  const expected = data?.expected_minutes || 480;
  const pct = Math.min(100, Math.round((worked / expected) * 100));
  const h = Math.floor(worked / 60);
  const m = worked % 60;
  const R = 34, C = 2 * Math.PI * R;

  const clock = async (type) => {
    setBusy(true);
    try {
      await api.post(`/attendance/clock/${type}`);
      toast.success(type === "in" ? "Entrata registrata" : "Uscita registrata");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Operazione non riuscita");
    } finally {
      setBusy(false);
    }
  };

  const statusLabel = data?.present
    ? `In servizio dalle ${fmtHM(data.since)}`
    : (data?.clocks_today || 0) > 0 ? "In pausa: hai timbrato l'uscita" : "Non hai ancora timbrato";

  return (
    <Card title="La mia giornata" icon={Clock} testId="myday-card"
          action={<CardLink to="/attendance" label="Presenze" testId="myday-open-attendance" />}>
      <div className="flex items-center gap-4">
        <div className="relative hidden md:block shrink-0" style={{ width: 84, height: 84 }}
             role="img" aria-label={`Ore lavorate: ${h} ore e ${m} minuti su ${expected / 60} previste`}>
          <svg width="84" height="84" viewBox="0 0 84 84" aria-hidden="true">
            <circle cx="42" cy="42" r={R} fill="none" stroke="var(--wl-border, #E8E8E6)" strokeWidth="7" />
            <circle cx="42" cy="42" r={R} fill="none" stroke="#F4F430" strokeWidth="7" strokeLinecap="round"
                    strokeDasharray={C} strokeDashoffset={C - (C * pct) / 100}
                    transform="rotate(-90 42 42)" />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center leading-none">
            <span className="text-[15px] font-semibold" style={{ color: "var(--wl-ink)" }} data-testid="myday-worked">
              {`${h}h ${String(m).padStart(2, "0")}`}
            </span>
            <span className="text-[9px] mt-0.5" style={{ color: "var(--wl-text-muted)" }}>
              {`su ${expected / 60}h`}
            </span>
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="md:hidden mb-2">
            <div className="flex items-baseline justify-between text-xs mb-1">
              <span className="font-semibold" style={{ color: "var(--wl-ink)" }}>{h}h {String(m).padStart(2, "0")}m</span>
              <span style={{ color: "var(--wl-text-muted)" }}>su {expected / 60}h</span>
            </div>
            <div className="h-2 rounded-full bg-subtle overflow-hidden" role="progressbar"
                 aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
              <div className="h-full rounded-full" style={{ width: `${pct}%`, background: "#F4F430" }} />
            </div>
          </div>
          <div className="flex items-center gap-1.5 text-sm mb-3" data-testid="myday-status">
            <span className={`w-2 h-2 rounded-full shrink-0 ${data?.present ? "bg-emerald-500" : "bg-neutral-300"}`} aria-hidden="true" />
            <span style={{ color: "var(--wl-text-secondary)" }}>{statusLabel}</span>
          </div>
          {clockAllowed ? (
            <div className="flex items-center gap-2 flex-wrap">
              {data?.present ? (
                <>
                  <button type="button" onClick={() => clock("out")} disabled={busy}
                          className="wl-btn wl-btn--ghost wl-btn--sm min-h-[44px] md:min-h-0"
                          style={{ border: "1px solid var(--wl-border)" }} data-testid="myday-pause">
                    <Coffee size={13} aria-hidden="true" /> Pausa
                  </button>
                  <button type="button" onClick={() => clock("out")} disabled={busy}
                          className="wl-btn wl-btn--primary wl-btn--sm min-h-[44px] md:min-h-0" data-testid="myday-out">
                    {busy ? <Loader2 size={13} className="animate-spin" /> : <LogOut size={13} aria-hidden="true" />} Esci
                  </button>
                </>
              ) : (
                <button type="button" onClick={() => clock("in")} disabled={busy}
                        className="wl-btn wl-btn--sm min-h-[44px] md:min-h-0 font-semibold"
                        style={{ background: "#F4F430", color: "#16161A", border: "1px solid #e3e320" }}
                        data-testid="myday-in">
                  {busy ? <Loader2 size={13} className="animate-spin" /> : <LogIn size={13} aria-hidden="true" />} Entra
                </button>
              )}
            </div>
          ) : (
            <div className="text-xs px-2.5 py-1.5 rounded-lg bg-subtle border border-border inline-flex items-center gap-1.5"
                 style={{ color: "var(--wl-text-secondary)" }} data-testid="myday-mobile-note">
              <Monitor size={12} aria-hidden="true" /> La timbratura si fa dal computer
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};

/* ─── Prossima riunione ─── */
export const MeetingCard = ({ meeting }) => {
  const navigate = useNavigate();
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((x) => x + 1), 30000);
    return () => clearInterval(t);
  }, []);

  if (!meeting) {
    return (
      <Card title="Prossima riunione" icon={Video} testId="meeting-card">
        <EmptyState phrase="Nessuna riunione in programma." actionLabel="Pianifica" to="/meetings"
                    testId="meeting-empty" />
      </Card>
    );
  }
  const diffMin = Math.max(0, Math.round((new Date(meeting.scheduled_at) - Date.now()) / 60000));
  const countdown = diffMin >= 1440
    ? `tra ${Math.floor(diffMin / 1440)}g ${Math.floor((diffMin % 1440) / 60)}h`
    : diffMin >= 60 ? `tra ${Math.floor(diffMin / 60)}h ${diffMin % 60}m`
    : diffMin > 0 ? `tra ${diffMin} min` : "adesso";
  return (
    <Card title="Prossima riunione" icon={Video} testId="meeting-card"
          action={<CardLink to="/meetings" label="Tutte" testId="meeting-open-all" />}>
      <div className="text-2xl font-semibold mb-1" style={{ color: "var(--wl-ink)" }} data-testid="meeting-countdown">
        {countdown}
      </div>
      <div className="text-sm font-medium truncate" style={{ color: "var(--wl-ink)" }}>{meeting.title}</div>
      <div className="text-xs mt-0.5" style={{ color: "var(--wl-text-secondary)" }}>
        {new Date(meeting.scheduled_at).toLocaleString("it-IT", { weekday: "short", hour: "2-digit", minute: "2-digit" })}
        {meeting.created_by_name && <> · organizza {meeting.created_by_name}</>}
      </div>
      <button type="button" onClick={() => navigate("/meetings")}
              className="wl-btn wl-btn--sm mt-3 font-semibold min-h-[44px] md:min-h-0"
              style={{ background: "#F4F430", color: "#16161A", border: "1px solid #e3e320" }}
              data-testid="meeting-join">
        <Video size={13} aria-hidden="true" /> Entra
      </button>
    </Card>
  );
};

/* ─── Aspetta te (admin) ─── */
const CountRow = ({ icon: Icon, label, count, to, testId }) => (
  <Link to={to} data-testid={testId}
        className="flex items-center justify-between gap-2 px-2.5 py-2 -mx-1 rounded-lg hover:bg-subtle transition-colors min-h-[44px] md:min-h-0">
    <span className="inline-flex items-center gap-2 text-sm" style={{ color: "var(--wl-ink)" }}>
      <Icon size={14} strokeWidth={1.8} aria-hidden="true" style={{ color: "var(--wl-text-muted)" }} />
      {label}
    </span>
    <span className="text-sm font-semibold px-2 py-0.5 rounded-full"
          style={{ background: "#F4F430", color: "#16161A" }}>{count}</span>
  </Link>
);

export const WaitingCard = ({ waiting }) => {
  const rows = [
    waiting?.absences_pending > 0 && { icon: Plane, label: "Ferie e permessi da approvare", count: waiting.absences_pending, to: "/attendance", testId: "waiting-absences" },
    waiting?.corrections_pending > 0 && { icon: AlarmClock, label: "Correzioni timbrature", count: waiting.corrections_pending, to: "/attendance", testId: "waiting-corrections" },
    waiting?.tickets_open > 0 && { icon: Inbox, label: waiting.tickets_scope === "miei" ? "I miei ticket aperti" : "Ticket da gestire", count: waiting.tickets_open, to: "/support?filtro=da_gestire", testId: "waiting-tickets" },
  ].filter(Boolean);
  return (
    <Card title="Aspetta te" icon={Inbox} testId="waiting-card">
      {rows.length === 0 ? (
        <EmptyState phrase="Tutto in ordine: niente in attesa di approvazione." testId="waiting-empty" />
      ) : (
        <div className="space-y-0.5">{rows.map((r) => <CountRow key={r.testId} {...r} />)}</div>
      )}
    </Card>
  );
};

/* ─── I miei compiti (operatore) ─── */
export const TasksCard = ({ tasks }) => {
  const rows = [
    tasks?.tickets_open_mine > 0 && { icon: MessageSquare, label: "Messaggi assegnati a me", count: tasks.tickets_open_mine, to: "/messages", testId: "tasks-tickets" },
    tasks?.docs_to_read > 0 && { icon: FileText, label: "Documenti da leggere", count: tasks.docs_to_read, to: "/documents", testId: "tasks-docs" },
    tasks?.courses_todo > 0 && { icon: GraduationCap, label: "Corsi da completare", count: tasks.courses_todo, to: "/formazione", testId: "tasks-courses" },
  ].filter(Boolean);
  return (
    <Card title="I miei compiti" icon={CheckSquare} testId="tasks-card">
      {rows.length === 0 ? (
        <EmptyState phrase="Nessun compito in sospeso, ottimo lavoro." testId="tasks-empty" />
      ) : (
        <div className="space-y-0.5">{rows.map((r) => <CountRow key={r.testId} {...r} />)}</div>
      )}
    </Card>
  );
};

/* ─── Messaggi ─── */
export const MessagesCard = ({ messages }) => {
  const items = messages?.items || [];
  return (
    <Card title="Messaggi" icon={MessageSquare} testId="messages-card"
          action={messages?.unread > 0 ? (
            <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full"
                  style={{ background: "#F4F430", color: "#16161A" }} data-testid="messages-unread-badge">
              {messages.unread} {messages.unread === 1 ? "nuovo" : "nuovi"}
            </span>
          ) : <CardLink to="/messages" label="Apri" testId="messages-open" />}>
      {items.length === 0 ? (
        <EmptyState phrase="Nessun messaggio recente." actionLabel="Scrivi al team" to="/messages" testId="messages-empty" />
      ) : (
        <div className="space-y-0.5">
          {items.map((m) => (
            <Link key={m.id} to="/messages" data-testid={`message-row-${m.id}`}
                  className="flex items-center gap-2.5 px-2.5 py-2 -mx-1 rounded-lg hover:bg-subtle transition-colors min-h-[44px] md:min-h-0">
              <span className={`w-2 h-2 rounded-full shrink-0 ${m.unread ? "bg-blue-500" : "bg-neutral-200"}`} aria-hidden="true" />
              <span className="flex-1 min-w-0">
                <span className={`block text-sm truncate ${m.unread ? "font-semibold" : ""}`} style={{ color: "var(--wl-ink)" }}>
                  {m.subject}
                </span>
                <span className="block text-[11px]" style={{ color: "var(--wl-text-muted)" }}>
                  {m.from_name} · {fmtRel(m.at)}
                </span>
              </span>
            </Link>
          ))}
        </div>
      )}
    </Card>
  );
};

/* ─── Scadenze in arrivo ─── */
export const DeadlinesCard = ({ deadlines }) => {
  const items = deadlines?.items || [];
  return (
    <Card title="Scadenze in arrivo" icon={CalendarClock} testId="deadlines-card">
      {items.length === 0 ? (
        <EmptyState phrase="Nessuna scadenza imminente." testId="deadlines-empty" />
      ) : (
        <div className="space-y-1">
          {items.map((d, i) => {
            const days = Math.ceil((new Date(d.due_date) - Date.now()) / 86400000);
            return (
              <div key={d.id || i} className="flex items-center justify-between gap-2 px-2.5 py-2 -mx-1 rounded-lg"
                   data-testid={`deadline-row-${i}`}>
                <span className="text-sm truncate" style={{ color: "var(--wl-ink)" }}>{d.title}</span>
                <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full shrink-0 ${days < 7 ? "bg-red-100 text-red-700" : ""}`}
                      style={days < 7 ? {} : i === 0 ? { background: "#F4F430", color: "#16161A" } : { background: "var(--wl-subtle, #f2f2f0)", color: "var(--wl-text-secondary)" }}>
                  {days <= 0 ? "oggi" : `${days} gg`}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
};

/* ─── Chi c'è oggi (admin) ─── */
const Initials = ({ name }) => (
  <span className="w-8 h-8 rounded-full bg-subtle text-[11px] font-semibold flex items-center justify-center ring-2 ring-surface"
        style={{ color: "var(--wl-ink)" }}>
    {name.split(" ").slice(0, 2).map((p) => p[0]).join("").toUpperCase()}
  </span>
);

export const TeamTodayCard = ({ team }) => {
  const c = team?.counts || {};
  return (
    <Card title="Chi c'è oggi" icon={Users} testId="team-today-card"
          action={<CardLink to="/attendance" label="Presenze" testId="team-open-attendance" />}>
      <div className="flex items-center gap-4 mb-3" data-testid="team-counts">
        {[["In servizio", c.present, "text-emerald-600"], ["Assenti", c.absent, "text-amber-600"], ["Non timbrato", c.not_clocked, ""]].map(([label, n, cls]) => (
          <div key={label}>
            <div className={`text-xl font-semibold ${cls}`} style={cls ? {} : { color: "var(--wl-ink)" }}>{n ?? 0}</div>
            <div className="text-[10px] uppercase tracking-wide" style={{ color: "var(--wl-text-muted)" }}>{label}</div>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {(team?.members || []).slice(0, 8).map((m) => (
          <span key={m.user_id} className="relative" title={`${m.name} — ${m.present ? "in servizio" : m.absence ? m.absence : "non timbrato"}`}
                data-testid={`team-avatar-${m.user_id}`}>
            <Initials name={m.name} />
            <span className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full ring-2 ring-surface ${m.present ? "bg-emerald-500" : m.absence ? "bg-amber-400" : "bg-neutral-300"}`} aria-hidden="true" />
          </span>
        ))}
        {(team?.members || []).length > 8 && (
          <span className="w-8 h-8 rounded-full bg-subtle text-[10px] font-semibold flex items-center justify-center"
                style={{ color: "var(--wl-text-secondary)" }}>
            +{team.members.length - 8}
          </span>
        )}
      </div>
    </Card>
  );
};

/* ─── Le mie richieste (operatore) ─── */
const ABS_LABEL = { ferie: "Ferie", permesso: "Permesso", malattia: "Malattia" };
const ABS_ICON = { ferie: Plane, permesso: AlarmClock, malattia: Stethoscope };
export const MyRequestsCard = ({ data }) => {
  const recent = data?.recent || [];
  return (
    <Card title="Le mie richieste" icon={Plane} testId="my-requests-card"
          action={<CardLink to="/attendance" label="Gestisci" testId="requests-open" />}>
      {data?.pending > 0 && (
        <div className="text-xs px-2.5 py-1.5 rounded-lg mb-2 inline-flex items-center gap-1.5 bg-amber-50 text-amber-700 border border-amber-200"
             data-testid="requests-pending">
          {data.pending} in attesa di approvazione
        </div>
      )}
      {recent.length === 0 && !data?.pending ? (
        <EmptyState phrase="Nessuna richiesta recente." actionLabel="Chiedi ferie o permessi" to="/attendance"
                    testId="requests-empty" />
      ) : (
        <div className="space-y-0.5">
          {recent.map((r) => {
            const Ic = ABS_ICON[r.type] || Plane;
            return (
              <div key={r.id} className="flex items-center justify-between gap-2 px-2.5 py-1.5 -mx-1 rounded-lg">
                <span className="inline-flex items-center gap-2 text-sm" style={{ color: "var(--wl-ink)" }}>
                  <Ic size={13} aria-hidden="true" style={{ color: "var(--wl-text-muted)" }} />
                  {ABS_LABEL[r.type] || r.type}
                  <span className="text-[11px]" style={{ color: "var(--wl-text-muted)" }}>
                    {r.permit_date || r.start_date}
                  </span>
                </span>
                <span className={`wl-badge ${r.status === "pending" ? "wl-badge--warning" : "wl-badge--success"}`}
                      style={{ fontSize: "10px" }}>
                  {r.status === "pending" ? "In attesa" : "Approvata"}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
};

/* ─── Formazione ─── */
const COURSE_STATUS = { completed: "Completato", in_progress: "In corso", not_started: "Da iniziare" };
export const TrainingCard = ({ training }) => (
  <Card title="Formazione" icon={GraduationCap} testId="training-card"
        action={<CardLink to="/formazione" label="Corsi" testId="training-open" />}>
    {(training?.courses || []).length === 0 ? (
      <EmptyState phrase="Nessun corso assegnato al momento." actionLabel="Richiedi un percorso" to="/formazione"
                  testId="training-empty" />
    ) : (
      <div className="space-y-2.5">
        {training.courses.map((c) => (
          <Link key={c.id} to="/formazione" className="block group" data-testid={`training-course-${c.id}`}>
            <div className="flex items-center justify-between gap-2 mb-1">
              <span className="text-sm truncate group-hover:underline" style={{ color: "var(--wl-ink)" }}>{c.title}</span>
              <span className="text-[10px] shrink-0" style={{ color: "var(--wl-text-muted)" }}>{COURSE_STATUS[c.status]}</span>
            </div>
            <div className="h-1.5 rounded-full bg-subtle overflow-hidden" role="progressbar"
                 aria-valuenow={c.progress} aria-valuemin={0} aria-valuemax={100}>
              <div className="h-full rounded-full transition-all"
                   style={{ width: `${c.progress}%`, background: c.status === "completed" ? "#22c55e" : "#F4F430" }} />
            </div>
          </Link>
        ))}
        {training.team_month && (
          <div className="text-xs pt-1.5 border-t border-border" style={{ color: "var(--wl-text-secondary)" }}
               data-testid="training-team-month">
            Il team questo mese: <strong style={{ color: "var(--wl-ink)" }}>{training.team_month.completed}/{training.team_month.total}</strong> con corsi completati
          </div>
        )}
      </div>
    )}
  </Card>
);

/* ─── Checklist di oggi ─── */
export const ChecklistCard = ({ checklist }) => {
  const [items, setItems] = useState(checklist?.items || []);
  const [doneCount, setDoneCount] = useState(checklist?.done_count || 0);
  useEffect(() => {
    setItems(checklist?.items || []);
    setDoneCount(checklist?.done_count || 0);
  }, [checklist]);

  const toggle = async (item) => {
    setItems((xs) => xs.filter((x) => x.id !== item.id));
    setDoneCount((n) => n + 1);
    try {
      await api.patch(`/checklists/${checklist.id}/items/${item.id}`, { done: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nel salvataggio");
      setItems((xs) => [item, ...xs]);
      setDoneCount((n) => n - 1);
    }
  };

  if (!checklist) {
    return (
      <Card title="Checklist di oggi" icon={CheckSquare} testId="checklist-card">
        <EmptyState phrase="Nessuna checklist attiva." actionLabel="Creane una" to="/checklist" testId="checklist-empty" />
      </Card>
    );
  }
  return (
    <Card title={checklist.title || "Checklist di oggi"} icon={CheckSquare} testId="checklist-card"
          action={<span className="text-[11px]" style={{ color: "var(--wl-text-muted)" }} data-testid="checklist-progress">
            {doneCount}/{checklist.total} fatte
          </span>}>
      {items.length === 0 ? (
        <EmptyState phrase="Tutto spuntato per oggi. Ben fatto!" testId="checklist-done" />
      ) : (
        <div className="space-y-0.5">
          {items.map((i) => (
            <label key={i.id} className="flex items-center gap-2.5 px-2 py-1.5 -mx-1 rounded-lg hover:bg-subtle cursor-pointer transition-colors min-h-[44px] md:min-h-0"
                   data-testid={`checklist-item-${i.id}`}>
              <input type="checkbox" checked={false} onChange={() => toggle(i)} className="w-4 h-4 shrink-0"
                     aria-label={`Segna come fatta: ${i.text}`} />
              <span className="text-sm" style={{ color: "var(--wl-ink)" }}>{i.text}</span>
            </label>
          ))}
        </div>
      )}
    </Card>
  );
};

/* ─── Attività recente ─── */
export const ActivityCard = ({ items, isAdmin }) => (
  <Card title={isAdmin ? "Attività recente" : "La mia attività"} icon={Activity} testId="activity-card"
        action={<CardLink to="/history" label="Storico" testId="activity-open" />}>
    {(items || []).length === 0 ? (
      <EmptyState phrase="Nessuna attività recente." actionLabel="Apri i servizi" to="/services" testId="activity-empty" />
    ) : (
      <div className="space-y-0.5">
        {items.map((r, i) => (
          <div key={i} className="flex items-center justify-between gap-2 px-2.5 py-1.5 -mx-1 rounded-lg"
               data-testid={`activity-row-${i}`}>
            <span className="min-w-0">
              <span className="block text-sm truncate" style={{ color: "var(--wl-ink)" }}>{r.service}</span>
              {isAdmin && r.user_email && (
                <span className="block text-[11px] truncate" style={{ color: "var(--wl-text-muted)" }}>{r.user_email}</span>
              )}
            </span>
            <span className="text-[11px] shrink-0" style={{ color: "var(--wl-text-muted)" }}>{fmtRel(r.at)}</span>
          </div>
        ))}
      </div>
    )}
  </Card>
);
